import React, { useState, useEffect } from "react";

const Slider = ({
  title = "Select billing frequency",
  options = ["Weekly", "Bi-weekly", "Monthly"],
  onChange,
  defaultValue = 0,
  disabled = false
}) => {
  const [selectedIndex, setSelectedIndex] = useState(defaultValue);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    setSelectedIndex(defaultValue);
  }, [defaultValue]);

  const handleChange = (e) => {
    if (disabled) return;
    const index = parseInt(e.target.value);
    setSelectedIndex(index);
    onChange?.(options[index]);
  };

  const handleMouseDown = () => {
    if (disabled) return;
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, []);

  const percentage = (selectedIndex / (options.length - 1)) * 100;
  const thumbLeft = `calc(${percentage}% - 12px)`;

  return (
    <div className="relative w-full flex flex-col items-start justify-start gap-6 text-left text-sm text-neutrals-neutral-900 font-open-sans-ff-body">
      <div className="relative font-semibold">{title}</div>

      <div className="w-[400px] relative h-[52px] text-base text-neutrals-neutral-300">
        {/* Slider Track (Base) */}
        <div className="absolute h-[46.15%] w-full top-0 right-0 bottom-[53.85%] left-0">
          {/* Full Track Background */}
          <div className="absolute h-[33.33%] w-full top-[33.33%] rounded-81xl bg-primary-primary-50 transition-colors duration-200" />
          
          {/* Selected Area Track */}
          <div
            className="absolute h-[33.33%] top-[33.33%] rounded-81xl bg-primary-primary-500 transition-all duration-300 ease-in-out"
            style={{ 
              width: `${percentage}%`,
              transform: isDragging ? 'scaleY(1.2)' : 'scaleY(1)'
            }}
          />

          {/* Circle Thumb */}
          <div 
            className="absolute h-full w-[6%] transition-transform duration-200 ease-in-out"
            style={{ 
              left: thumbLeft,
              transform: isDragging ? 'scale(1.1)' : 'scale(1)'
            }}
          >
            <div 
              className={`absolute top-0 left-[33.33%] h-5 w-5 rounded-full bg-white border-solid border-[2px] shadow-md transition-all duration-200 ease-in-out ${
                isDragging 
                  ? 'border-primary-primary-600 shadow-lg' 
                  : 'border-primary-primary-500 hover:border-primary-primary-600'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'}`}
            />
          </div>
        </div>

        {/* Labels */}
        <div className="absolute h-[42.31%] w-full top-[57.69%] flex flex-row items-center justify-between">
          {options.map((label, index) => (
            <div
              key={index}
              className={`relative transition-all duration-200 ${
                index === selectedIndex 
                  ? "text-primary-primary-500 font-semibold transform scale-110" 
                  : "text-neutrals-neutral-300 hover:text-neutrals-neutral-400"
              } ${disabled ? 'opacity-50' : ''}`}
            >
              {label}
            </div>
          ))}
        </div>

        {/* Hidden Range Input */}
        <input
          type="range"
          min={0}
          max={options.length - 1}
          value={selectedIndex}
          onChange={handleChange}
          onMouseDown={handleMouseDown}
          className={`absolute top-0 left-0 w-full h-full opacity-0 ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default Slider;